import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Url } from '@rossum/api-client';
import { DocumentsDownloadsType } from '@rossum/api-client/documents';
import { openModal } from '../../../redux/modules/modal/actions';
import {
  AttachmentStatus,
  hasFailedProcessing,
} from '../../../types/attachmentStatus';
import { useDownloadOriginalDocuments } from '../../tasks/hooks/useDownloadOriginalDocuments';

export type DownloadableDocument = {
  url: Url;
  attachmentStatus: AttachmentStatus;
};

/**
 * Wraps {@link useDownloadOriginalDocuments} with the confirmation dialog
 * required for documents that failed automated processing: their original file
 * has not been checked for potential risks.
 *
 * A single affected document is enough to ask for confirmation; confirming
 * downloads everything that was requested, cancelling downloads nothing.
 */
export const useGuardedDownloadOriginalDocuments = () => {
  const dispatch = useDispatch();
  const { mutate: downloadOriginalDocuments } = useDownloadOriginalDocuments();

  return useCallback(
    (
      documents: DownloadableDocument[],
      { type }: { type?: DocumentsDownloadsType } = {}
    ) => {
      const download = () =>
        downloadOriginalDocuments({
          documents: documents.map(({ url }) => url),
          type,
        });

      const failedCount = documents.filter(({ attachmentStatus }) =>
        hasFailedProcessing(attachmentStatus)
      ).length;

      if (failedCount === 0) {
        return download();
      }

      return dispatch(
        openModal({
          textId:
            documents.length === 1
              ? 'unverifiedFileDownload'
              : 'unverifiedFilesDownload',
          values: { count: failedCount },
          confirmType: 'Danger',
          onConfirm: download,
        })
      );
    },
    [dispatch, downloadOriginalDocuments]
  );
};
